import { useMemo } from 'react';
import { t } from '@app/i18n';
import { useLocale } from '@app/i18n/useLocale';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { PASSWORD_MAX, type LoginFormValues } from './LoginModel';

/**
 * Validação local de T1 (FR-004): formato de e-mail e **presença** da senha
 * habilitam **Entrar**. O e-mail é normalizado para minúsculas no próprio campo
 * (o contrato guarda minúsculas), então aqui só se checa o formato — o usuário
 * nunca leva erro por ter digitado maiúscula.
 *
 * **Sem mínimo de força aqui, de propósito** (FR-004 revisado, decisão P13):
 * a política de senha vale para **definir** senha (T22), não para conferir uma
 * existente. Recusar por comprimento no login tranca quem tem senha legítima
 * anterior a uma mudança de política, e conta a política a quem ainda não
 * entrou. O contrato (`LoginRequest.senha`) declara a ausência do mínimo.
 */
export function loginSchema(): yup.ObjectSchema<LoginFormValues> {
  return yup.object({
    email: yup
      .string()
      .required(t('login.form.validation.emailRequired'))
      .email(t('login.form.validation.emailInvalid'))
      .lowercase()
      .strict(false),
    senha: yup
      .string()
      .required(t('login.form.validation.passwordRequired'))
      .max(PASSWORD_MAX, t('login.form.validation.passwordMax', { max: PASSWORD_MAX })),
    lembrar: yup.boolean().required(),
  });
}

export function useLoginValidation() {
  const locale = useLocale();
  // O lint não enxerga a leitura do idioma feita pelo `t()` lá dentro.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const schema = useMemo(() => loginSchema(), [locale]);
  return {
    resolver: yupResolver(schema),
    // `onChange`: o botão **Entrar** só habilita quando os dois campos passam.
    mode: 'onChange' as const,
  };
}
